import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  checkOut,
  getBedsHistory,
  getCamps,
  getCheckouts,
  markBedClean,
} from 'features/accommodation/accommodationSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import {
  IGetAllBedsHiistorySingleResponse,
  IGetAllExpectedCheckoutsSingleResponse,
} from 'types/api/accommodationApi.interface'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { JustSpinner } from 'components/Loading/Loading'
import { Container, ContainerHeader } from '../Accommodation.style'
import { TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { areDatesSameDay, formatDateForDisplay } from 'utils/helpers'
import { BedIcon } from '../icons/BedIcon'
import { OccupiedBedIcon } from '../icons/OccupiedBedIcon'
import { BlockedIcon } from '../icons/BlockedIcon'
import { DirtyIcon } from '../icons/DirtyIcon'
import { ClockIcon } from '../icons/ClockIcon'
import { ExclamationMarkIcon } from '../icons/ExclamationMarkIcon'
import { PersonIcon } from '../icons/PersonIcon'
import { LocationIcon } from '../icons/LocationIcon'
import { NextIcon } from '../icons/NextIcon'
import { ExtendStayModal } from './ExtendStayModal'
import { aggregateCounts, buildOccupantMap, computeCamps, flattenBeds, BED_DIRTY } from '../accommodationStats'
import {
  StatBand,
  SectionStack,
  SectionTitleRow,
  SectionTitleGroup,
  SectionCount,
  TodaySectionContent,
  TodayRow,
  RowIdentity,
  RowName,
  RowLocation,
  RowMeta,
  RowActions,
  CleanRow,
  EmptyState,
  LoaderWrap,
} from './AccommodationToday.styled'

const C = {
  accent: tokens.accent.base,
  available: tokens.status.success.fg,
  occupied: tokens.status.info.fg,
  dirty: tokens.status.warning.fg,
  blocked: tokens.status.danger.fg,
}

const occupantName = (item: IGetAllBedsHiistorySingleResponse | IGetAllExpectedCheckoutsSingleResponse) => {
  if (item.user) return `${item.user.firstName} ${item.user.lastName}`
  if (item.accommodationGuest)
    return `${item.accommodationGuest.firstName} ${item.accommodationGuest.lastName}`
  return 'Unknown'
}

const daysOverdue = (expected: string) => {
  const diff = Date.now() - new Date(expected).getTime()
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  return days < 1 ? 1 : days
}

const LocationLine = ({
  item,
}: {
  item: IGetAllBedsHiistorySingleResponse | IGetAllExpectedCheckoutsSingleResponse
}) => (
  <RowLocation>
    <LocationIcon />
    {item.campName || '/'}
    <NextIcon />
    {item.blockName || '/'}
    <NextIcon />
    {item.roomName || '/'}
    <NextIcon />
    {item.bed?.label || '/'}
  </RowLocation>
)

export const AccommodationToday = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { camps, bedsHistory, checkOuts, isLoading } = useSelector(
    (state: RootState) => state.accomodation,
  )
  const [extendId, setExtendId] = useState<number | null>(null)

  const refetch = () => {
    dispatch(getCamps({ page: 1, limit: 99 }))
    dispatch(getBedsHistory({ page: 1, limit: 999 }))
    dispatch(getCheckouts({ page: 1, limit: 999, date: new Date() }))
  }

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const occupancies = bedsHistory?.data || []
  const departures = checkOuts?.data || []

  const active = useMemo(
    () => occupancies.filter((o) => !o.actualCheckOutTimestamp),
    [occupancies],
  )

  const arrivals = useMemo(
    () => active.filter((o) => o.checkInTimestamp && areDatesSameDay(o.checkInTimestamp, new Date())),
    [active],
  )

  const overstays = useMemo(
    () =>
      active.filter(
        (o) =>
          o.expectedCheckOutTimestamp &&
          new Date(o.expectedCheckOutTimestamp).getTime() < Date.now(),
      ),
    [active],
  )

  const occupantByBed = useMemo(() => buildOccupantMap(occupancies), [occupancies])
  const computed = useMemo(
    () => computeCamps(camps?.data || [], occupantByBed),
    [camps, occupantByBed],
  )
  const totals = useMemo(() => aggregateCounts(computed), [computed])
  const dirtyBeds = useMemo(
    () => flattenBeds(computed).filter((b) => b.effectiveState === BED_DIRTY),
    [computed],
  )

  const dirtyCount = totals.dirty
  const blockedCount = totals.blocked
  const occupancyPct = totals.occupancy

  const handleCheckOut = (id: number) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(checkOut(id))
          refetch()
        },
        setParams: {
          type: ModalTypes.BeforeSabe,
        },
        word: 'check out this person',
      }),
    )
  }

  const handleMarkClean = (bedId: number) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(markBedClean(bedId))
          refetch()
        },
        setParams: {
          type: ModalTypes.BeforeSabe,
        },
        word: 'mark this bed as clean',
      }),
    )
  }

  return (
    <>
      <StatBand>
        <MetricCard
          layout='inline'
          label='Arrivals today'
          value={String(arrivals.length)}
          icon={<PersonIcon />}
          accentColor={C.available}
        />
        <MetricCard
          layout='inline'
          label='Departures today'
          value={String(departures.length)}
          icon={<OccupiedBedIcon />}
          accentColor={C.occupied}
        />
        <MetricCard
          layout='inline'
          label='Overstays'
          value={String(overstays.length)}
          icon={<ExclamationMarkIcon />}
          accentColor={overstays.length ? C.blocked : C.available}
          trailing={overstays.length ? <StatusPill status='danger'>Action</StatusPill> : undefined}
        />
        <MetricCard
          layout='inline'
          label='Dirty'
          value={String(dirtyCount)}
          icon={<DirtyIcon />}
          accentColor={C.dirty}
        />
        <MetricCard
          layout='inline'
          label='Blocked'
          value={String(blockedCount)}
          icon={<BlockedIcon />}
          accentColor={C.blocked}
          trailing={blockedCount ? <StatusPill status='warning'>Review</StatusPill> : undefined}
        />
        <MetricCard
          layout='inline'
          label='Occupancy'
          value={`${occupancyPct}%`}
          icon={<BedIcon />}
          accentColor={C.accent}
        />
      </StatBand>

      {isLoading && !occupancies.length ? (
        <LoaderWrap>
          <JustSpinner />
        </LoaderWrap>
      ) : (
        <SectionStack>
          <Container>
            <ContainerHeader padding>
              <SectionTitleRow>
                <SectionTitleGroup>
                  <ExclamationMarkIcon />
                  <TitleText>Overstays</TitleText>
                  <SectionCount>{overstays.length}</SectionCount>
                </SectionTitleGroup>
              </SectionTitleRow>
            </ContainerHeader>
            <TodaySectionContent>
              {overstays.length ? (
                overstays.map((o) => (
                  <TodayRow $danger key={o.id}>
                    <RowIdentity>
                      <RowName>{occupantName(o)}</RowName>
                      <StatusPill status={o.personType === 1 ? 'info' : 'neutral'}>
                        {o.personType === 1 ? 'Employee' : 'Guest'}
                      </StatusPill>
                    </RowIdentity>
                    <LocationLine item={o} />
                    <RowMeta>
                      <StatusPill status='danger' dot>
                        {daysOverdue(o.expectedCheckOutTimestamp)}d overdue
                      </StatusPill>
                    </RowMeta>
                    <RowActions>
                      <Button variant='secondary' size='sm' onClick={() => setExtendId(o.id)}>
                        Extend
                      </Button>
                      <Button variant='success' size='sm' onClick={() => handleCheckOut(o.id)}>
                        Check out
                      </Button>
                    </RowActions>
                  </TodayRow>
                ))
              ) : (
                <EmptyState>No overstays</EmptyState>
              )}
            </TodaySectionContent>
          </Container>

          <Container>
            <ContainerHeader padding>
              <SectionTitleRow>
                <SectionTitleGroup>
                  <OccupiedBedIcon />
                  <TitleText>Departures today</TitleText>
                  <SectionCount>{departures.length}</SectionCount>
                </SectionTitleGroup>
              </SectionTitleRow>
            </ContainerHeader>
            <TodaySectionContent>
              {departures.length ? (
                departures.map((d) => (
                  <TodayRow key={d.id}>
                    <RowIdentity>
                      <RowName>{occupantName(d)}</RowName>
                      <StatusPill status={d.personType === 1 ? 'info' : 'neutral'}>
                        {d.personType === 1 ? 'Employee' : 'Guest'}
                      </StatusPill>
                    </RowIdentity>
                    <LocationLine item={d} />
                    <RowMeta>Out: {formatDateForDisplay(d.expectedCheckOutTimestamp, true)}</RowMeta>
                    <RowActions>
                      <Button variant='success' size='sm' onClick={() => handleCheckOut(d.id)}>
                        Check out
                      </Button>
                    </RowActions>
                  </TodayRow>
                ))
              ) : (
                <EmptyState>No departures today</EmptyState>
              )}
            </TodaySectionContent>
          </Container>

          <Container>
            <ContainerHeader padding>
              <SectionTitleRow>
                <SectionTitleGroup>
                  <PersonIcon />
                  <TitleText>Arrivals today</TitleText>
                  <SectionCount>{arrivals.length}</SectionCount>
                </SectionTitleGroup>
              </SectionTitleRow>
            </ContainerHeader>
            <TodaySectionContent>
              {arrivals.length ? (
                arrivals.map((a) => (
                  <TodayRow key={a.id}>
                    <RowIdentity>
                      <RowName>{occupantName(a)}</RowName>
                      <StatusPill status={a.personType === 1 ? 'info' : 'neutral'}>
                        {a.personType === 1 ? 'Employee' : 'Guest'}
                      </StatusPill>
                    </RowIdentity>
                    <LocationLine item={a} />
                    <RowMeta>In: {formatDateForDisplay(a.checkInTimestamp, true)}</RowMeta>
                    <RowActions>
                      <StatusPill status='success'>Checked in</StatusPill>
                    </RowActions>
                  </TodayRow>
                ))
              ) : (
                <EmptyState>No arrivals today</EmptyState>
              )}
            </TodaySectionContent>
          </Container>

          <Container>
            <ContainerHeader padding>
              <SectionTitleRow>
                <SectionTitleGroup>
                  <ClockIcon color={C.dirty} />
                  <TitleText>To clean</TitleText>
                  <SectionCount>{dirtyBeds.length}</SectionCount>
                </SectionTitleGroup>
              </SectionTitleRow>
            </ContainerHeader>
            <TodaySectionContent>
              {dirtyBeds.length ? (
                dirtyBeds.map((bed) => (
                  <CleanRow key={bed.bedId}>
                    <RowIdentity>
                      <RowName>{bed.label}</RowName>
                      <RowLocation>
                        <LocationIcon />
                        {bed.campName || '/'}
                        <NextIcon />
                        {bed.blockName || '/'}
                        <NextIcon />
                        {bed.roomName || '/'}
                      </RowLocation>
                    </RowIdentity>
                    <Button variant='secondary' size='sm' onClick={() => handleMarkClean(bed.bedId)}>
                      Mark clean
                    </Button>
                  </CleanRow>
                ))
              ) : (
                <EmptyState>Nothing to clean right now</EmptyState>
              )}
            </TodaySectionContent>
          </Container>
        </SectionStack>
      )}

      {extendId !== null && (
        <ExtendStayModal
          id={extendId}
          closeModal={() => {
            setExtendId(null)
            refetch()
          }}
        />
      )}
    </>
  )
}
